import React, { Suspense } from "react";
import { ActivityIndicator, Platform, StyleSheet, useWindowDimensions, View } from "react-native";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import Feather from "@expo/vector-icons/Feather";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTheme } from "@/hooks/useTheme";
import { brandSurface } from "@/components/FahaliBrand";
import { PROTOTYPE_SCREENS_LIVE } from "@/screens/V7/prototypeScreens";

// V7 Fahali screens — Paper/Pro aesthetic
const V7Home = React.lazy(() => import("@/screens/V7/V7HomeScreen"));
const V7Intelligence = React.lazy(() => import("@/screens/V7/V7IntelligenceScreen"));
const V7Incidents = React.lazy(() => import("@/screens/V7/SituationWatchScreen"));
const V7Portfolio = React.lazy(() => import("@/screens/V7/V7PortfolioScreen"));
const Profile = React.lazy(() => import("@/screens/LedgerScreenDeferred"));

function deferred(Component: React.ComponentType<any>) {
  return function DeferredTab(props: any) {
    return (
      <Suspense fallback={
          <View style={styles.loading}>
            <ActivityIndicator accessibilityLabel="Loading page" />
          </View>
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };
}

const HomeScreen = deferred(V7Home);
const IntelligenceScreen = deferred(V7Intelligence);
const IncidentsScreen = deferred(V7Incidents);
const PortfolioScreen = deferred(V7Portfolio);
const ProfileScreen = deferred(Profile);

export type MainTabParamList = {
  Dashboard: undefined;
  Intelligence: undefined;
  Incidents: undefined;
  Portfolio: undefined;
  Profile: undefined;
  // Route aliases for backward compatibility
  Attention: undefined;
  Intel: undefined;
  Watchlist: undefined;
  Alerts: undefined;
  History: undefined;
};

const Tab = createBottomTabNavigator<MainTabParamList>();

export default function MainTabNavigator() {
  const { theme, isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const desktop = Platform.OS === "web" && width >= 1024;
  const bottomInset = Platform.OS === "web" ? 0 : insets.bottom;

  // Stitch Prototype theme: Neon emerald active, slate inactive
  const activeColor = isDark ? "#2DD4BF" : "#0D9488";
  const inactiveColor = isDark ? "#64748B" : "#94A3B8";

  return (
    <Tab.Navigator
      initialRouteName="Dashboard"
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: activeColor,
        tabBarInactiveTintColor: inactiveColor,
        tabBarPosition: desktop ? "left" : "bottom",
        tabBarVariant: desktop ? "material" : "uikit",
        tabBarLabelPosition: desktop ? "beside-icon" : "below-icon",
        tabBarStyle: {
          ...brandSurface(isDark),
          backgroundColor: isDark ? "#0B0F1A" : "#F7F3EA",
          borderRadius: 0,
          position: "relative",
          borderColor: isDark ? "#1C2538" : "rgba(0,0,0,0.08)",
          borderTopWidth: desktop ? 0 : 1,
          borderRightWidth: desktop ? 1 : 0,
          elevation: 0,
          ...(desktop
            ? { display: "none" as const }
            : { height: 62 + bottomInset, paddingBottom: bottomInset + 4, paddingTop: 6 }),
        },
        tabBarLabelStyle: { fontSize: 12, fontWeight: "700", letterSpacing: 0.5 },
        tabBarItemStyle: { minHeight: 48 },
      }}
    >
      <Tab.Screen
        name="Dashboard"
        component={HomeScreen}
        options={{
          title: "ATTENTION",
          tabBarAccessibilityLabel: "ATTENTION",
          tabBarIcon: ({ color, size }) => <Feather name="target" color={color} size={size - 1} />,
        }}
      />
      <Tab.Screen
        name="Intelligence"
        component={IntelligenceScreen}
        options={{
          title: "INTEL",
          tabBarAccessibilityLabel: "INTEL",
          tabBarIcon: ({ color, size }) => <Feather name="activity" color={color} size={size - 1} />,
        }}
      />
      {/* Incidents mounts the Stitch SituationWatch prototype, whose figures are
          hardcoded (see screens/V7/prototypeScreens.ts). Hidden until it reads the API. */}
      {PROTOTYPE_SCREENS_LIVE && (
      <Tab.Screen
        name="Incidents"
        component={IncidentsScreen}
        options={{
          title: "INCIDENTS",
          tabBarAccessibilityLabel: "INCIDENTS",
          tabBarIcon: ({ color, size }) => <Feather name="alert-triangle" color={color} size={size - 1} />,
        }}
      />
      )}
      <Tab.Screen
        name="Portfolio"
        component={PortfolioScreen}
        options={{
          title: "PORTFOLIO",
          tabBarAccessibilityLabel: "PORTFOLIO",
          tabBarIcon: ({ color, size }) => <Feather name="briefcase" color={color} size={size - 1} />,
        }}
      />
      {/* Settings (2026-10-01, founder): no "You" tab — the initials avatar in
          V7Header is the one way in. The route stays registered so /profile,
          the avatar and every navigate("Profile") keep resolving. */}
      <Tab.Screen
        name="Profile"
        component={ProfileScreen}
        options={{
          title: "Settings",
          tabBarButton: () => null,
          tabBarItemStyle: { display: "none" },
        }}
      />

      {/* Hidden legacy aliases to keep route compatibility 100% intact */}
      <Tab.Screen
        name="Watchlist"
        component={IntelligenceScreen}
        options={{
          tabBarButton: () => null,
          tabBarItemStyle: { display: "none" },
        }}
      />
      <Tab.Screen
        name="Alerts"
        component={IntelligenceScreen}
        options={{
          tabBarButton: () => null,
          tabBarItemStyle: { display: "none" },
        }}
      />
      <Tab.Screen
        name="History"
        component={ProfileScreen}
        options={{
          tabBarButton: () => null,
          tabBarItemStyle: { display: "none" },
        }}
      />
    </Tab.Navigator>
  );
}

const styles = StyleSheet.create({
  loading: { flex: 1, alignItems: "center", justifyContent: "center" },
});
